
import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Users, Bike, Check, ChevronRight } from 'lucide-react';

const SolutionsPage: React.FC = () => {
  const solutions = [
    {
      id: 'solar-all',
      title: '태양광발전 통합 지원 플랫폼',
      subtitle: '재생에너지 전환을 위한 원스톱 솔루션',
      icon: <Sun className="h-10 w-10 text-btBlue" />,
      needs: [
        '태양광 발전사업을 시작하려는 기업',
        'RE100 이행을 준비하는 기업',
        '재생에너지 PPA를 찾는 수요기업',
        '효율적인 발전소 관리가 필요한 사업자'
      ],
      features: [
        '발전사업자-수요기업 스마트 매칭',
        '사업 타당성 분석 도구',
        '인허가 프로세스 지원',
        '발전량 모니터링 및 관리'
      ],
      effects: [
        '재생에너지 도입 기간 단축',
        '사업 운영 효율성 향상',
        '탄소배출 저감 및 ESG 경영 실현'
      ],
      color: 'blue',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?q=80&w=2070&auto=format&fit=crop'
    },
    {
      id: 'citizen-solar',
      title: '주민참여형 태양광발전 서비스',
      subtitle: '지역 주민이 함께 만드는 에너지 전환',
      icon: <Users className="h-10 w-10 text-btGreen" />,
      needs: [
        '에너지 자립을 꿈꾸는 마을 공동체',
        '지역 상생 모델이 필요한 지자체',
        '소액으로 태양광에 투자하고 싶은 시민'
      ],
      features: [
        '주민 참여 프로세스 설계 및 관리',
        '투명한 수익 배분 시스템',
        '지역 맞춤형 발전소 운영 지원',
        '주민 소통 플랫폼'
      ],
      effects: [
        '지역 일자리 창출',
        '주민 수용성 향상',
        '지속 가능한 에너지 커뮤니티 구축'
      ],
      color: 'green',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?q=80&w=2070&auto=format&fit=crop'
    },
    {
      id: 'bike-platform',
      title: '자전거 타기 활성화 플랫폼',
      subtitle: '수송부문 탄소저감을 위한 스마트 모빌리티',
      icon: <Bike className="h-10 w-10 text-btOrange" />,
      needs: [
        '시민 기후행동을 독려하는 지자체',
        '임직원 건강 및 ESG를 고민하는 기업',
        '친환경 라이프스타일을 추구하는 개인'
      ],
      features: [
        '자전거 이용 추적 및 인증',
        '탄소저감량 실시간 계산',
        '참여 인센티브 시스템',
        '지자체-시민 연계 프로그램'
      ],
      effects: [
        '개인 탄소발자국 감소',
        '건강한 라이프스타일 촉진',
        '도시 친환경 교통 문화 조성'
      ],
      color: 'orange',
      image: 'https://images.unsplash.com/photo-1485965120184-e220f721d03e?q=80&w=2070&auto=format&fit=crop'
    }
  ];

  return (
    <div className="bg-white">
      {/* Page Header */}
      <section className="bg-gray-900 py-24 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-black mb-6">BlueTerra Solutions</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            IT 기술로 재생에너지와 일상을 연결하여 누구나 탄소중립에 참여할 수 있는 미래를 설계합니다.
          </p>
        </div>
      </section>

      {/* Solutions List */}
      <section className="py-24 space-y-32">
        {solutions.map((sol, idx) => (
          <div key={sol.id} id={sol.id} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-start ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={`${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="flex items-center gap-4 mb-6">
                  <div className={`p-4 rounded-2xl shadow-sm ${sol.color === 'blue' ? 'bg-blue-50' : sol.color === 'green' ? 'bg-green-50' : 'bg-orange-50'}`}>
                    {sol.icon}
                  </div>
                  <div>
                    <span className={`text-sm font-bold uppercase tracking-widest ${sol.color === 'blue' ? 'text-btBlue' : sol.color === 'green' ? 'text-btGreen' : 'text-btOrange'}`}>
                      Solution {idx + 1}
                    </span>
                    <h2 className="text-3xl font-black text-gray-900">{sol.title}</h2>
                  </div>
                </div>
                <p className="text-xl font-medium text-gray-600 mb-10">{sol.subtitle}</p>

                <div className="space-y-10">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <ChevronRight className="h-5 w-5 text-btBlue" /> 이런 분들께 필요합니다
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {sol.needs.map((n, i) => (
                        <div key={i} className="flex items-center gap-2 text-gray-600 text-sm">
                          <Check className="h-4 w-4 text-btGreen" /> {n}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">주요 기능</h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {sol.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-gray-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-btBlue"></div> {f}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">기대 효과</h3>
                    <div className="flex flex-wrap gap-4">
                      {sol.effects.map((e, i) => (
                        <span key={i} className="px-4 py-2 bg-btBlue/5 text-btBlue text-sm font-bold rounded-full border border-btBlue/10">
                          {e}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className={`relative ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className={`absolute -inset-4 ${sol.color === 'blue' ? 'bg-blue-600/5' : sol.color === 'green' ? 'bg-green-600/5' : 'bg-orange-600/5'} rounded-3xl`}></div>
                <img 
                  src={sol.image}
                  alt={sol.title} 
                  className="relative z-10 rounded-2xl shadow-xl w-full h-[500px] object-cover bg-gray-200"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1485965120184-e220f721d03e?q=80&w=2070&auto=format&fit=crop";
                  }}
                />
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Target Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-black mb-16">고객 대상별 맞춤형 서비스</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="p-10 bg-white rounded-2xl shadow-sm border-t-8 border-btBlue">
              <h3 className="text-xl font-bold mb-4">지자체/공공기관</h3>
              <p className="text-gray-600 mb-6 text-sm">지역 에너지 자립 및 시민 기후행동 활성화를 위한 데이터 플랫폼 제공</p>
              <ul className="text-left text-xs space-y-2 text-gray-500">
                <li>• 에너지 효율 모니터링</li>
                <li>• 탄소중립 성과 가시화</li>
              </ul>
            </div>
            <div className="p-10 bg-white rounded-2xl shadow-sm border-t-8 border-btOrange">
              <h3 className="text-xl font-bold mb-4">기업 (ESG 경영)</h3>
              <p className="text-gray-600 mb-6 text-sm">RE100 이행 및 임직원 탄소저감 활동 관리를 통한 ESG 경쟁력 확보</p>
              <ul className="text-left text-xs space-y-2 text-gray-500">
                <li>• 탄소 배출량 관리</li>
                <li>• 사회적 가치 창출 인증</li>
              </ul>
            </div>
            <div className="p-10 bg-white rounded-2xl shadow-sm border-t-8 border-btGreen">
              <h3 className="text-xl font-bold mb-4">시민/개인</h3>
              <p className="text-gray-600 mb-6 text-sm">일상 속 탄소중립 참여와 그에 따른 실질적인 리워드 혜택 제공</p>
              <ul className="text-left text-xs space-y-2 text-gray-500">
                <li>• 친환경 활동 리워드</li>
                <li>• 커뮤니티 태양광 참여</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SolutionsPage;
